import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  Description as ContractsIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

export default function Dashboard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // State for real metrics
  const [metrics, setMetrics] = useState([
    { title: 'Active Clients', value: 0, icon: <PeopleIcon />, color: '#1976d2', loading: true },
    { title: 'Total Vendors', value: 0, icon: <BusinessIcon />, color: '#388e3c', loading: true },
    { title: 'Total Services', value: 0, icon: <BuildIcon />, color: '#f57c00', loading: true },
    { title: 'Active Contracts', value: 0, icon: <ContractsIcon />, color: '#7b1fa2', loading: true },
  ]);

  // Fetch real data
  useEffect(() => {
    const unsubscribes = [];

    // Fetch clients
    const fetchClients = () => {
      try {
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('status', '==', 'Active'));
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const count = querySnapshot.size;
          setMetrics(prev => prev.map(metric => 
            metric.title === 'Active Clients' 
              ? { ...metric, value: count, loading: false }
              : metric
          ));
        });
        
        return unsubscribe;
      } catch (error) {
        console.error('Error fetching clients:', error);
        setMetrics(prev => prev.map(metric => 
          metric.title === 'Active Clients' 
            ? { ...metric, loading: false }
            : metric
        ));
      }
    };

    // Fetch costs
    const fetchCosts = () => {
      try {
        const costsRef = collection(db, 'costs');
        const q = query(costsRef);
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const count = querySnapshot.size;
          setMetrics(prev => prev.map(metric => 
            metric.title === 'Total Services' 
              ? { ...metric, value: count, loading: false }
              : metric
          ));
        });
        
        return unsubscribe;
      } catch (error) {
        console.error('Error fetching costs:', error);
        setMetrics(prev => prev.map(metric => 
          metric.title === 'Total Services' 
            ? { ...metric, loading: false }
            : metric
        ));
      }
    };

    // Fetch contracts (active ones)
    const fetchContracts = () => {
      try {
        const contractsRef = collection(db, 'contracts');
        const q = query(contractsRef, where('status', '==', 'Active'));
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          // Count active contracts (assuming any contract in the collection is active)
          // You can add additional filtering here based on contract status if needed
          const count = querySnapshot.size;
          setMetrics(prev => prev.map(metric => 
            metric.title === 'Active Contracts' 
              ? { ...metric, value: count, loading: false }
              : metric
          ));
        });
        
        return unsubscribe;
      } catch (error) {
        console.error('Error fetching contracts:', error);
        setMetrics(prev => prev.map(metric => 
          metric.title === 'Active Contracts' 
            ? { ...metric, loading: false }
            : metric
        ));
      }
    };

    // Fetch suppliers (vendors in entities collection)
    const fetchSuppliers = () => {
      try {
        const entitiesRef = collection(db, 'entities');
        const q = query(
          entitiesRef,
          where('entityType', '==', 'vendor'),
          where('status', '==', 'active')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const count = querySnapshot.size;
          setMetrics(prev => prev.map(metric => 
            metric.title === 'Total Vendors' 
              ? { ...metric, value: count, loading: false }
              : metric
          ));
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        setMetrics(prev => prev.map(metric => 
          metric.title === 'Total Vendors' 
            ? { ...metric, loading: false }
            : metric
        ));
      }
    };

    // Execute fetches
    if (currentUser) {
      unsubscribes.push(fetchClients());
      unsubscribes.push(fetchCosts());
      unsubscribes.push(fetchContracts());
      unsubscribes.push(fetchSuppliers());
    }

    // Cleanup subscriptions
    return () => {
      unsubscribes.forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [currentUser]);

  // Mock recent activity data
  const recentClients = [
    { name: 'Acme Corporation', contact: 'John Smith' },
    { name: 'Tech Solutions Ltd', contact: 'Sarah Johnson' },
    { name: 'Global Industries', contact: 'Mike Chen' },
    { name: 'Innovation Hub', contact: 'Emily Davis' },
    { name: 'Future Systems', contact: 'Alex Thompson' },
  ];

  const recentCosts = [
    { name: 'Cloud Migration', margin: 25 },
    { name: 'Data Analytics', margin: 30 },
    { name: 'Security Audit', margin: 35 },
    { name: 'System Integration', margin: 20 },
    { name: 'Business Consulting', margin: 40 },
  ];

  // Quick actions with navigation
  const quickActions = [
    { 
      title: 'Add Lead', 
      icon: <PeopleIcon />, 
      color: '#1976d2',
      onClick: () => navigate('/leads?new=1')
    },
    { 
      title: 'Add New Service', 
      icon: <BuildIcon />, 
      color: '#388e3c',
      onClick: () => navigate('/costs?new=1')
    },
    { 
      title: 'View Contracts', 
      icon: <ContractsIcon />, 
      color: '#f57c00',
      onClick: () => navigate('/contracts')
    },
  ];

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
          🏢 UCENA Agency Management System
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome to your business management dashboard
        </Typography>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                borderLeft: `4px solid ${metric.color}`,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: metric.color,
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                {metric.icon}
              </Avatar>
              <Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {metric.loading ? (
                    <CircularProgress size={24} sx={{ color: metric.color }} />
                  ) : (
                    metric.value
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {metric.title}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Recent Activity Section */}
      {/* <Typography variant="h4" component="h2" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <AssessmentIcon sx={{ mr: 2, color: 'primary.main' }} />
        Recent Activity
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
                Recent Clients
              </Typography>
              <List>
                {recentClients.map((client, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        <PeopleIcon fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography fontWeight="bold">{client.name}</Typography>}
                      secondary={client.contact}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 'bold' }}>
                Recent Costs
              </Typography>
              <List>
                {recentCosts.map((cost, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                        <BuildIcon fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography fontWeight="bold">{cost.name}</Typography>}
                      secondary={`${cost.margin}% margin`}
                    />
                    <TrendingUpIcon color="success" />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* <Divider sx={{ my: 4 }} /> */}

      {/* Quick Actions */}
      <Typography variant="h4" component="h2" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <AddIcon sx={{ mr: 2, color: 'primary.main' }} />
        Quick Actions
      </Typography>

      <Grid container spacing={3}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={action.icon}
              onClick={action.onClick}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                backgroundColor: action.color,
                '&:hover': {
                  backgroundColor: action.color,
                  filter: 'brightness(90%)',
                },
              }}
            >
              {action.title}
            </Button>
          </Grid>
        ))}
      </Grid>

 
    </Container>
  );
} 